/* /login — sign in (prototype pages3.js pgLogin). Email + password, then a one-time-code step,
 * demo quick-login per role, `?next=` redirect, logged-in users go straight home. */
import { useId, useRef, useState, type FormEvent } from "react";
import { useSeo } from "../../seo/useSeo";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { homeFor, useSession } from "../../app/session";
import { ApiError } from "../../data/provider";
import type { Role } from "../../data/types";
import { useI18n } from "../../i18n";
import { Alert, Button, Field, Icon, Input, SelectField, Skeleton, errMsg, useToast } from "../../ui";
import { isBusinessCategory, type BusinessCategory, type CountryCode } from "../../data/types";
import { COUNTRIES, countryOf, parseCountry } from "../../domain/country";
import { inCountry } from "./mktCountry";
import { industryOf } from "../../domain/industry";
import DEMO_LOGINS from "../../../../contracts/demo-logins.json";
import { categoryOptions } from "../industry/categoryOptions";
import { AuthSide, AuthTop } from "./components/AuthLayout";
import CheckInbox from "./components/CheckInbox";

export const DEMO_PASSWORD = "StartHR@2026";
export const DEMO_ACCOUNTS: [Role, string, string, string][] = [
  ["owner", "abdullah.harbi@alwaha.sa", "Owner", "المالك"],
  ["hr", "sara.otaibi@alwaha.sa", "HR Officer", "مسؤول الموارد البشرية"],
  ["accountant", "mohammed.zahrani@alwaha.sa", "Accountant", "المحاسب"],
  ["employee", "rajesh.nair@alwaha.sa", "Employee", "الموظف"],
  ["admin", "nawaf@starthr.gulfunionozone.com", "StartHR admin", "إدارة ستارت HR"],
];
const DEMO_OTP = "482196";
type DemoAccounts = Record<"owner" | "hr" | "accountant" | "employee", string>;
type DemoEntry = { name: [string, string]; accounts: DemoAccounts };
const LOGINS = DEMO_LOGINS as unknown as { companies: Record<BusinessCategory, DemoEntry>; countries: Partial<Record<CountryCode, Record<BusinessCategory, DemoEntry>>> };
/** Saudi demo company per business category (contracts/demo-logins.json, generated from the seed). */
export const DEMO_COMPANIES = LOGINS.companies;
export const DEFAULT_DEMO_CATEGORY: BusinessCategory = "trading";
/** The demo company of a business category in a country (every GCC country has one per category). */
export function demoCompany(category: BusinessCategory, country: CountryCode = "SA"): DemoEntry {
  return (country === "SA" ? undefined : LOGINS.countries[country]?.[category]) ?? DEMO_COMPANIES[category];
}
/** The demo login of a role in a category's demo company in a country (admin is platform-wide). */
export function demoEmail(category: BusinessCategory, role: Role, country: CountryCode = "SA"): string {
  if (role === "admin") return DEMO_ACCOUNTS.find((a) => a[0] === "admin")![1];
  return demoCompany(category, country).accounts[role];
}

/** Only same-app absolute paths are accepted as a redirect target. */
export function safeNext(next: string | null): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/login")) return null;
  return next;
}

export default function Login() {
  useSeo("/login");
  const { t, L } = useI18n();
  const { user, ready, role, login } = useSession();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const toast = useToast();
  const next = safeNext(params.get("next"));
  const emailId = useId(), pwId = useId();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [unverified, setUnverified] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [otp, setOtp] = useState<string[]>(DEMO_OTP.split(""));
  const [fe, setFe] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const demoParam = params.get("demo");
  const [demoCat, setDemoCat] = useState<BusinessCategory>(isBusinessCategory(demoParam) ? demoParam : DEFAULT_DEMO_CATEGORY);
  const [demoCc, setDemoCc] = useState<CountryCode>(parseCountry(params.get("country")) ?? "SA");
  const dc = countryOf(demoCc);
  const demo = demoCompany(demoCat, demoCc);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [justSignedIn, setJustSignedIn] = useState(false);

  if (!ready) return <div className="auth"><main className="auth-main"><Skeleton /></main></div>;
  if (user && !justSignedIn) return <Navigate to={next ?? homeFor(role)} replace />;

  const finish = (r: Role, name: string) => {
    setJustSignedIn(true);
    toast(L("Signed in as ", "تم الدخول باسم ") + name);
    nav(next ?? homeFor(r), { replace: true });
  };

  const doLogin = async (em: string, pw: string, code?: string, key = "form") => {
    setBusy(key); setFormErr(null); setFe({});
    try {
      const u = await login({ email: em.trim(), password: pw, otp: code });
      finish(u.role, L(u.name, u.nameAr));
    } catch (e) {
      setStep(0);
      if (e instanceof ApiError && e.code === "email_not_verified") setUnverified(em.trim().toLowerCase());
      else if (e instanceof ApiError && e.status === 401) setFormErr(L("Wrong email or password. Check them and try again.", "البريد الإلكتروني أو كلمة المرور غير صحيحة. تحقق وحاول مرة أخرى."));
      else if (e instanceof ApiError && e.status === 422 && e.fields) setFe(e.fields);
      else setFormErr(errMsg(e));
    } finally { setBusy(null); }
  };

  const onContinue = (ev: FormEvent) => {
    ev.preventDefault();
    const f: Record<string, string> = {};
    if (!email.trim()) f.email = L("Enter your work email", "أدخل بريد العمل");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) f.email = L("Enter a valid email address", "أدخل بريدًا إلكترونيًا صحيحًا");
    if (!password) f.password = L("Enter your password", "أدخل كلمة المرور");
    setFe(f); setFormErr(null);
    if (Object.keys(f).length) return;
    setOtp(DEMO_OTP.split(""));
    setStep(1);
  };

  const onVerify = (ev: FormEvent) => {
    ev.preventDefault();
    const code = otp.join("");
    if (!/^\d{6}$/.test(code)) { setFe({ otp: L("Enter all 6 digits", "أدخل الأرقام الستة") }); return; }
    void doLogin(email, password, code);
  };

  const setDigit = (i: number, v: string) => {
    const d = v.replace(/\D/g, "").slice(-1);
    const nx = otp.slice(); nx[i] = d; setOtp(nx);
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  };

  return (
    <div className="auth" data-testid="login">
      {dc.code === "SA"
        ? <AuthSide title={L("Payroll, GOSI and Iqamas, handled.", "الرواتب والتأمينات والإقامات، بكل سهولة.")} text={L("HR and payroll built for Saudi businesses with 5 to 250 employees. Arabic and English, WPS-ready, Nitaqat-aware.", "نظام موارد بشرية ورواتب للمنشآت السعودية من 5 إلى 250 موظفًا. بالعربية والإنجليزية، جاهز لحماية الأجور ونطاقات.")} />
        : <AuthSide title={L(`Payroll, ${dc.si.short[0]} and residence permits, handled.`, `الرواتب و${dc.si.short[1]} والإقامات، بكل سهولة.`)} text={L(`HR and payroll for businesses in ${dc.short[0]} with 5 to 250 employees. Arabic and English, ${dc.wps.name[0]}-ready, ${dc.localisation.short[0]}-aware, paid in ${dc.currency.code}.`, `نظام موارد بشرية ورواتب للمنشآت في ${dc.short[1]} من 5 إلى 250 موظفًا. بالعربية والإنجليزية، جاهز لـ${dc.wps.name[1]} و${dc.localisation.short[1]}، والرواتب بـ${dc.currency.word[1]}.`)} />}
      <main className="auth-main">
        <AuthTop country={dc} />
        <div className="auth-card">
          <div>
            <h1 style={{ fontSize: 26 }}>{t("Sign in")}</h1>
            <p className="muted" style={{ margin: "6px 0 0" }}>{L("Welcome back to StartHR", "مرحبًا بعودتك إلى ستارت HR")}</p>
          </div>
          {unverified && <CheckInbox email={unverified} title={L("Confirm your email first", "أكّد بريدك أولًا")} onBack={() => setUnverified(null)} backLabel={L("Back to sign in", "العودة لتسجيل الدخول")} />}
          {!unverified && next && <Alert tone="info" tint title={L("Sign in to continue", "سجّل الدخول للمتابعة")} desc={L("You'll go back to the page you opened.", "ستعود إلى الصفحة التي فتحتها.")} />}
          {formErr && <Alert tone="bad" title={formErr} testId="login-error" />}

          {unverified ? null : step === 0 ? (
            <form className="stack" onSubmit={onContinue} noValidate data-testid="login-form">
{dc.code === "SA" && <>
              <button type="button" className="btn block nafath" style={{ minHeight: 48 }} onClick={() => toast(L("Nafath sign-in is switched off in this demo — use email and password.", "الدخول عبر نفاذ غير مفعل في العرض — استخدم البريد وكلمة المرور."), "info")}>
                <Icon name="shield" /><span>{t("Sign in with Nafath")}</span>
              </button>
              <div className="or">{L("or", "أو")}</div>
              </>}
              <Field label={L("Work email", "بريد العمل")} htmlFor={emailId} err={fe.email}>
                <Input id={emailId} type="email" autoComplete="username" dir="ltr" value={email} bad={!!fe.email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              </Field>
              <Field label={L("Password", "كلمة المرور")} htmlFor={pwId} err={fe.password}>
                <div className="affix">
                  <Input id={pwId} type={showPw ? "text" : "password"} autoComplete="current-password" dir="ltr" value={password} bad={!!fe.password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                  <button type="button" className="end ibtn" aria-label={showPw ? L("Hide password", "إخفاء كلمة المرور") : L("Show password", "إظهار كلمة المرور")} onClick={() => setShowPw(!showPw)}><Icon name="eye" /></button>
                </div>
              </Field>
              <div className="row" style={{ justifyContent: "flex-end", marginTop: -6 }}>
                <Link className="sm" to={"/forgot-password" + (email.trim() ? "?email=" + encodeURIComponent(email.trim()) : "")} data-testid="link-forgot">{L("Forgot password?", "نسيت كلمة المرور؟")}</Link>
              </div>
              <Button type="submit" variant="pri" className="block" data-testid="login-continue">{t("Continue")}</Button>
            </form>
          ) : (
            <form className="stack" onSubmit={onVerify} noValidate data-testid="otp-form">
              <div className="field">
                <span className="flabel">{L("Enter the 6-digit code sent to ", "أدخل الرمز المكون من 6 أرقام المرسل إلى ")}<b dir="ltr">{email}</b></span>
                <div className="otp">
                  {otp.map((d, i) => (
                    <input key={i} ref={(el) => { otpRefs.current[i] = el; }} inputMode="numeric" maxLength={1} value={d} aria-label={L("Digit ", "الرقم ") + (i + 1)} data-testid={"otp-" + i}
                      onChange={(e) => setDigit(i, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus(); }} />
                  ))}
                </div>
                {fe.otp ? <span className="err" role="alert"><Icon name="alert" className="s" />{fe.otp}</span> : <span className="hint">{L("Demo: the code is pre-filled · Resend in 0:42", "عرض: الرمز معبأ مسبقًا · إعادة الإرسال خلال 0:42")}</span>}
              </div>
              <Button type="submit" variant="pri" className="block" loading={busy === "form"} data-testid="login-verify">{t("Verify")}</Button>
              <Button variant="ghost" className="block" onClick={() => { setStep(0); setFe({}); }}>{t("Back")}</Button>
            </form>
          )}

          <div className="stack" style={{ gap: 8 }} data-testid="signup-cta">
            <div className="or">{L("New to StartHR?", "جديد على ستارت HR؟")}</div>
            <Link className="btn block" style={{ minHeight: 44 }} to={"/setup" + (dc.slug ? "?country=" + dc.slug : "") + (demoParam && isBusinessCategory(demoParam) ? (dc.slug ? "&" : "?") + "category=" + demoParam : "")} data-testid="login-signup"><Icon name="plus" className="s" /><span>{L("Sign up", "إنشاء حساب")}</span></Link>
          </div>

          <div className="stack demo-box" style={{ gap: 10 }} data-testid="demo-logins">
            <div className="or">{L("Demo accounts", "حسابات العرض")}</div>
            <p className="xs muted" style={{ margin: 0 }}>{L("Explore a ready-made company with real-looking staff, payroll and documents. Pick a country, a business category and a role.", "استكشف منشأة جاهزة بموظفين ورواتب ومستندات تجريبية. اختر الدولة والنشاط والدور.")}</p>
            <div className="demo-pick">
              <SelectField label={L("Country", "الدولة")} value={demoCc} onChange={(v) => { const c = parseCountry(v); if (c) setDemoCc(c); }} testId="demo-country"
                options={COUNTRIES.map((c): [string, string] => [c.code, c.flag + " " + L(...c.name)])} />
              <SelectField label={L("Business category", "نشاط المنشأة")} value={demoCat} onChange={(v) => { if (isBusinessCategory(v)) setDemoCat(v); }} options={categoryOptions(L, false)} testId="demo-category" />
            </div>
            <div className="demo-co" data-testid="demo-company">
              <span className="demo-flag" aria-hidden="true">{dc.flag}</span>
              <span className="demo-co-t">
                <span className="xs muted">{L("Demo company", "منشأة العرض")} · {L(...dc.short)}</span>
                <b data-testid="demo-company-name">{L(...demo.name)}</b>
                <span className="xs muted" data-testid="demo-company-facts">{L(...dc.cities[0].slice(1) as [string, string])} · {dc.currency.code} · {L(...dc.si.short)} · {L(...dc.wps.name)}</span>
                <Link className="xs" to={inCountry("/industries/" + demoCat, dc)}>{L(`See the ${industryOf(demoCat)!.name[0]} pack`, `اطلع على حزمة ${industryOf(demoCat)!.name[1]}`)}</Link>
              </span>
            </div>
            <span className="flabel" style={{ textAlign: "center" }}>{L("Open the demo as", "افتح العرض بصفة")}</span>
            <div className="row" style={{ justifyContent: "center", gap: 6 }} role="group" aria-label={L("Open the demo as", "افتح العرض بصفة")}>
              {DEMO_ACCOUNTS.map(([r, , en, ar]) => (
                <Button key={r} variant="sm" loading={busy === r} disabled={!!busy && busy !== r} onClick={() => doLogin(demoEmail(demoCat, r, demoCc), DEMO_PASSWORD, undefined, r)} data-testid={"demo-" + r}>{L(en, ar)}</Button>
              ))}
            </div>
            <p className="xs muted" style={{ margin: 0, textAlign: "center" }}>{L("Password for every demo account: ", "كلمة المرور لكل حسابات العرض: ")}<code dir="ltr">{DEMO_PASSWORD}</code></p>
          </div>

        </div>
      </main>
    </div>
  );
}
